import { useCallback, useEffect, useState } from "react";

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: boolean;
  retry: () => void;
}

/**
 * Simulates an async content request so that loading / empty / error states
 * are wired up consistently across pages. Swap the loader for a real API call later.
 */
export function useAsyncData<T>(loader: () => T, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    const timer = setTimeout(() => {
      try {
        const result = loader();
        if (active) {
          setData(result);
          setLoading(false);
        }
      } catch {
        if (active) {
          setError(true);
          setLoading(false);
        }
      }
    }, 260);
    return () => {
      active = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const retry = useCallback(() => setTick((v) => v + 1), []);

  return { data, loading, error, retry };
}

export default useAsyncData;